import { Box, Image, Skeleton } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { ReplaceMenuTabIndex } from 'modules/Editor/store/editorSettingsSlice.types';
import { LinkWrapper } from 'modules/common/components/Link';
import { ReactElement, memo, useCallback, useId, useMemo, useRef } from 'react';
import { Subset } from 'types/object.types';
import { convertTintToFeFuncRgbIntercept, getColorWithAlphaString } from 'utils/color';
import { generateShapeStyle } from 'widgets/Widget.helpers';
import { ReadOnlyRenderType } from 'widgets/Widget.types';
import {
  ReadOnlyWidgetBase,
  WidgetBase,
  WidgetSideMenuSettings,
  WidgetToolbar,
  useEditor,
  useWidget,
} from 'widgets/sdk';

import { CropData, CropHandler } from './Crop';
import { ClipPathFrameSvg, FrameShape, generateClipPathId, generateClipPathUrl, isSquareFrame } from './Frame';
import { CROP_BUTTON_ID } from './ImageWidget.config';
import { cropToAbsolutePosition, toStyleFiltersString, useImageLoader } from './ImageWidget.helpers';
import { ImageLayoutProps, ImageWidgetData } from './ImageWidget.types';
import { ImageWidgetSideMenuSettings } from './ImageWidgetSideMenuSettings';
import { ImageWidgetToolbarMenu } from './ImageWidgetToolbar';
import { DROP_SHADOW_CONFIG } from './Toolbar/ShadowOptions/ShadowOptions.config';
import { useImageResize } from './useImageResize';

export const ImageWidget = (): ReactElement => {
  const { url, frame, rotateDeg, widgetId } = useWidget<ImageWidgetData>();
  const { isCropView, openReplaceWidgetMenu } = useEditor();
  const { isLoading } = useImageLoader(url);
  const imageRectRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const onOpenReplaceWidgetMenu = useCallback(() => {
    openReplaceWidgetMenu(ReplaceMenuTabIndex.UPLOAD);
  }, [openReplaceWidgetMenu]);

  const { onImageResize, onImageResizeEnd } = useImageResize({
    rotateDeg,
    widgetId,
    imageRectRef,
    imageRef,
    frame,
  });

  return (
    <WidgetBase onDoubleClick={onOpenReplaceWidgetMenu} onResize={onImageResize} onResizeEnd={onImageResizeEnd}>
      <WidgetToolbar>
        <ImageWidgetToolbarMenu />
      </WidgetToolbar>
      <WidgetSideMenuSettings>
        <ImageWidgetSideMenuSettings />
      </WidgetSideMenuSettings>
      {isLoading ? (
        <Skeleton w='100%' h='100%' />
      ) : (
        <>{isCropView ? <CropContainer /> : <ImageLayout imageRectRef={imageRectRef} imageRef={imageRef} />}</>
      )}
    </WidgetBase>
  );
};

const CropContainer = () => {
  const { url, crop, frame, imageRect, widgetId, altText, updateWidget } = useWidget<ImageWidgetData>();
  const { widthPx: imageWidthPx, heightPx: imageHeightPx } = imageRect;

  const {
    zoom,
    disableCropView,
    disableEditorKeyboardShortcuts,
    enableEditorKeyboardShortcuts,
    widgetRefs,
    boundingBox,
  } = useEditor();
  const widgetBaseRef: HTMLElement = widgetRefs[widgetId]?.element;

  const onCropClose = useCallback(
    ({ crop, position, newImageDimension }: CropData) => {
      enableEditorKeyboardShortcuts();

      if (!crop || !position) return;
      const widthPx = (newImageDimension?.widthPx || imageWidthPx) - crop.left - crop.right;
      const heightPx = (newImageDimension?.heightPx || imageHeightPx) - crop.top - crop.bottom;

      const { leftPx, topPx } = position;
      const widget: Subset<ImageWidgetData> = {
        widthPx,
        heightPx,
        leftPx,
        topPx,
        crop,
        imageRect: {
          widthPx: newImageDimension?.widthPx ?? imageWidthPx,
          heightPx: newImageDimension?.heightPx ?? imageHeightPx,
        },
      };

      updateWidget(widget);

      // Prevent widget bounding box to get out of sync when cropping with keyboard
      setTimeout(() => boundingBox.updateRect(), 0);
    },
    [updateWidget, enableEditorKeyboardShortcuts, imageHeightPx, imageWidthPx, boundingBox],
  );

  const closeCrop = useCallback(
    (e?: Event) => {
      if (e && e.target && (e.target as HTMLElement).id === CROP_BUTTON_ID) return;
      disableCropView();
    },
    [disableCropView],
  );

  return (
    <CropHandler
      isCropAspectRatioLocked={isSquareFrame(frame)}
      closeCrop={closeCrop}
      onCropOpen={disableEditorKeyboardShortcuts}
      onCropClose={onCropClose}
      imageWidth={imageWidthPx}
      imageHeight={imageHeightPx}
      altText={altText}
      url={url}
      crop={crop}
      zoom={zoom}
      widgetBaseRef={widgetBaseRef}
    />
  );
};

export const ReadOnlyImageWidget = (): ReactElement => {
  const { url, hyperlink, isDecorative, renderType } = useWidget<ImageWidgetData>();
  const { isLoading } = useImageLoader(url);

  // Only wrap the widget with a link if its rendered with the structure tree list renderer
  // If the image is marked as decorative - do not wrap it with a link
  const isTreeRenderer =
    renderType === ReadOnlyRenderType.StructrueTreeList || renderType === ReadOnlyRenderType.ExportStructureTreeList;
  const hasHyperlink = isTreeRenderer && !isDecorative;

  return (
    <ReadOnlyWidgetBase>
      {isLoading ? (
        <Skeleton w='100%' h='100%' />
      ) : (
        <LinkWrapper hyperlink={hasHyperlink ? hyperlink : undefined}>
          <ImageLayout />
        </LinkWrapper>
      )}
    </ReadOnlyWidgetBase>
  );
};

const ImageLayout = ({ imageRectRef, imageRef }: ImageLayoutProps) => {
  const {
    url,
    crop,
    filter = {},
    frame,
    imageRect,
    opacity,
    altText,
    isDecorative,
    mirror = {},
  } = useWidget<ImageWidgetData>();
  const { filterIntensity = 50, effect = {}, appliedDropShadowName = 'None', dropShadowIntensity = 50 } = filter;
  const { widthPx: w, heightPx: h } = imageRect;
  const containerRef = useRef<HTMLDivElement>(null);

  // the id needs to be unique per component instance - as there can be multiple of the same widget on the same page -  widgetId will clash
  const id = useId();
  const uniqueClipPathId = generateClipPathId(id);
  const rgb = useMemo(() => convertTintToFeFuncRgbIntercept(effect?.tint ?? 50), [effect]);
  const filterString = useMemo(
    () => (filter?.effect ? toStyleFiltersString(effect, filterIntensity, id) : ''),
    [filterIntensity, filter, effect, id],
  );
  const shadowFilter = useMemo(
    () => DROP_SHADOW_CONFIG[appliedDropShadowName](dropShadowIntensity),
    [appliedDropShadowName, dropShadowIntensity],
  );

  // Only apply alt text if specified or marked as decorative
  const altAttributes: { alt?: string } = {};
  if (altText || isDecorative) altAttributes['alt'] = isDecorative ? '' : altText;

  const [top, right, bottom, left] = cropToAbsolutePosition(crop);
  const hasClipPathFrame = !!FrameShape[frame] && frame !== FrameShape.None;
  const clipPathUrl = hasClipPathFrame ? generateClipPathUrl(uniqueClipPathId) : '';
  const style = {
    top,
    left,
    right,
    bottom,
    width: w,
    height: h,
    opacity,
    ...generateShapeStyle(mirror),
  } as {};
  return (
    <Box w={'full'} h={'full'} filter={shadowFilter}>
      <Box position='absolute' clipPath={clipPathUrl} overflow='hidden' w='100%' height='100%' ref={containerRef}>
        {hasClipPathFrame && <ClipPathFrameSvg frame={frame} id={uniqueClipPathId} />}
        <div data-testid='image-rect' className='image-rect' ref={imageRectRef} style={style}>
          <SvgTintFilter rgb={rgb} id={id} />
          <ImageElement
            draggable='false'
            ref={imageRef}
            src={url}
            alt={altAttributes.alt}
            filter={filterString}
            fallback={<Skeleton w='full' height='full' />}
          />
        </div>
        <ColorOverlayBox />
      </Box>
    </Box>
  );
};

const SvgTintFilter = memo(({ rgb, id }: { rgb: { r: number; g: number; b: number } | null; id: string }) => (
  <svg width='0' height='0'>
    <defs>
      <filter id={`vg_filter_id__canvas__${id}`}>
        <feComponentTransfer>
          <feFuncR type='linear' intercept={rgb?.r ?? 0}></feFuncR>
          <feFuncG type='linear' intercept={rgb?.g ?? 0}></feFuncG>
          <feFuncB type='linear' intercept={rgb?.b ?? 0}></feFuncB>
        </feComponentTransfer>
      </filter>
    </defs>
  </svg>
));

SvgTintFilter.displayName = 'SvgTintFilter';

const ColorOverlayBox = () => {
  const { filter = {} } = useWidget<ImageWidgetData>();
  const {
    colorOverlay: { isEnabled: isOverlayEnabled, color: overlayColor = '', opacity: overlayIntensity = 0 } = {},
  } = filter;
  return isOverlayEnabled ? (
    <StyledColorOverlayBox
      backgroundColor={getColorWithAlphaString(overlayColor, overlayIntensity / 100)}
      data-testid='color-overlay-box'
    />
  ) : (
    <></>
  );
};

const StyledColorOverlayBox = styled(Box)`
  width: 100%;
  height: 100%;
  position: absolute;
  background-color: ${(props) => props.backgroundColor};
`;

const ImageElement = styled(Image)`
  transform: translateZ(0px);
  backface-visibility: hidden;
  position: absolute;
  user-select: none;
  height: 100%;
  width: 100%;
  object-fit: fill;
`;
